import type { QueryClient } from '@tanstack/react-query';
import { createRoute, Outlet, redirect } from '@tanstack/react-router';
import { type InternalAxiosRequestConfig, isAxiosError } from 'axios';
import { type FC, lazy, type ReactNode, Suspense } from 'react';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { AUTH_ROUTES } from '@/features/auth/consts/routes';
import { clearAuthSession } from '@/features/auth/utils/clear-auth-session';
import {
  isPastClearDeadline,
  refreshSessionOnce,
} from '@/features/auth/utils/refresh-session';
import {
  resetTransientBackoff,
  scheduleClearDeadline,
  scheduleTokenRefresh,
  scheduleTransientRetry,
} from '@/features/auth/utils/schedule-token-refresh';
import { AUTH_ROUTE_ID } from '@/integrations/tanstack-router/auth-route-id';
import { rootRoute } from '@/root-route';
import { getQueryShiftPeriodDisplayTimeOptions } from '@/services/use-query-shift-period-display-time';
import { SERVER_ERROR_CODES } from '~/generated/server-error-codes';
import type { UsersSchema } from './integrations/idb/schemas/users';
import {
  reattachApiRecorder,
  startApiRecording,
} from './features/rrweb/utils/api-recorder';
import { startRecording } from './features/rrweb/utils/recording-manager';
import {
  getBoneyardToken,
  IS_BONEYARD_BUILD,
} from './integrations/boneyard/get-boneyard-token';
import { FeatureFlagProvider } from './integrations/feature-flags/feature-flag-provider';
import { getCurrentUser } from './integrations/idb/models/users';
import { services } from './services';

const redirectToLogin = () => {
  const currentUrl =
    window.location.pathname + window.location.search + window.location.hash;
  const url = new URL(AUTH_ROUTES.login, window.location.origin);

  url.searchParams.set('redirect', currentUrl);
  window.location.href = url.toString();
};

const redirectToUnauthorized = () => {
  window.location.href = AUTH_ROUTES.unauthorized;
};

const blockAfterRedirect = () => new Promise<never>(() => {});

const AUTHORIZATION = 'Authorization';

type AuthTokens = Pick<UsersSchema, 'token' | 'refreshToken'>;
type SetupServicesAuth = (params: {
  token: string;
  userId: string;
  queryClient: QueryClient;
}) => void;
type RefreshScheduling = {
  queryClient: QueryClient;
  userId: string;
  setupServicesAuth: SetupServicesAuth;
};

const clearAtDeadline = async (
  expectedTokenExpiredAt: Date,
  queryClient: QueryClient,
) => {
  const currentUser = await getCurrentUser();

  if (
    currentUser &&
    currentUser.tokenExpiredAt.getTime() !== expectedTokenExpiredAt.getTime() &&
    currentUser.tokenExpiredAt.getTime() > Date.now()
  ) {
    return;
  }

  await clearAuthSession(queryClient);
  redirectToLogin();
};

const scheduleDeadline = (queryClient: QueryClient, tokenExpiredAt: Date) => {
  scheduleClearDeadline({
    tokenExpiredAt,
    onDeadline: () => clearAtDeadline(tokenExpiredAt, queryClient),
  });
};

const applyTokensAndScheduleNext = (
  { queryClient, userId, setupServicesAuth }: RefreshScheduling,
  tokens: AuthTokens,
  tokenExpiredAt: Date,
) => {
  setupServicesAuth({ token: tokens.token, userId, queryClient });
  scheduleTokenRefresh({
    tokenExpiredAt,
    onRefresh: (scheduledFor) =>
      proactiveTokenRefresh(
        userId,
        setupServicesAuth,
        queryClient,
        scheduledFor,
      ),
  });
  scheduleDeadline(queryClient, tokenExpiredAt);
};

const scheduleRetry = (
  { queryClient, userId, setupServicesAuth }: RefreshScheduling,
  tokenExpiredAt: Date,
) => {
  scheduleTransientRetry({
    tokenExpiredAt,
    onRefresh: (scheduledFor) =>
      proactiveTokenRefresh(
        userId,
        setupServicesAuth,
        queryClient,
        scheduledFor,
      ),
  });
};

type RefreshNavigation<T> = {
  authenticated: () => T | Promise<T>;
  retryable: () => T | Promise<T>;
  signedOut: () => T | Promise<T>;
};

const dispatchRefreshOutcome = async <T,>(
  scheduling: RefreshScheduling,
  outcome: Awaited<ReturnType<typeof refreshSessionOnce>>,
  navigation: RefreshNavigation<T>,
): Promise<T> => {
  if (outcome.kind === 'signed-out') {
    await clearAuthSession(scheduling.queryClient);

    return await navigation.signedOut();
  }

  return await match(outcome)
    .with(
      { kind: 'authenticated', persistenceFailed: false },
      ({ tokens, tokenExpiredAt }) => {
        resetTransientBackoff();
        applyTokensAndScheduleNext(scheduling, tokens, tokenExpiredAt);

        return Promise.resolve(navigation.authenticated());
      },
    )
    .with(
      { kind: 'authenticated', persistenceFailed: true },
      ({ tokens, tokenExpiredAt }) => {
        setupServicesAuth({
          token: tokens.token,
          userId: scheduling.userId,
          queryClient: scheduling.queryClient,
        });
        scheduleRetry(scheduling, tokenExpiredAt);
        scheduleDeadline(scheduling.queryClient, tokenExpiredAt);

        return Promise.resolve(navigation.authenticated());
      },
    )
    .with({ kind: 'retryable' }, ({ tokenExpiredAt }) => {
      scheduleRetry(scheduling, tokenExpiredAt);

      return Promise.resolve(navigation.retryable());
    })
    .exhaustive();
};

const proactiveTokenRefresh = async (
  userId: string,
  setupServicesAuth: SetupServicesAuth,
  queryClient: QueryClient,
  scheduledFor: Date,
) => {
  const outcome = await refreshSessionOnce({
    source: 'scheduled',
    scheduledFor,
  });

  await dispatchRefreshOutcome(
    { userId, setupServicesAuth, queryClient },
    outcome,
    {
      authenticated: () => undefined,
      retryable: () => undefined,
      signedOut: () => redirectToLogin(),
    },
  );
};

const setupServicesAuth: SetupServicesAuth = ({
  token,
  userId,
  queryClient,
}) => {
  services.forEach((service) => {
    service.interceptors.request.clear();
    service.interceptors.request.use(
      (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
        if (token) {
          config.headers.set(AUTHORIZATION, `Bearer ${token}`);
        }

        return config;
      },
      (error: unknown) => Promise.reject(error),
    );

    service.interceptors.response.clear();
    service.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (
          !isAxiosError(error) ||
          (error.response?.status !== 401 && error.response?.status !== 403)
        ) {
          return Promise.reject(error);
        }

        const statusCode = z
          .object({ statusCode: z.number() })
          .safeParse(error.response.data);

        if (
          error.response.status === 403 ||
          !statusCode.success ||
          statusCode.data.statusCode !== SERVER_ERROR_CODES.AUTH_TOKEN_EXPIRED
        ) {
          redirectToUnauthorized();

          return blockAfterRedirect();
        }

        return dispatchRefreshOutcome(
          { userId, setupServicesAuth, queryClient },
          await refreshSessionOnce({
            source: 'unauthorized',
            attemptedAccessToken: token,
          }),
          {
            authenticated: () => Promise.reject(error),
            retryable: () => Promise.reject(error),
            signedOut: () => {
              redirectToLogin();

              return blockAfterRedirect();
            },
          },
        );
      },
    );
  });
  reattachApiRecorder();
};

const AppLayout = lazy(() => import('./app-layout'));

const AuthenticatedLayout: FC<{ children: ReactNode }> = ({ children }) => (
  <Suspense>
    <FeatureFlagProvider>
      <AppLayout>{children}</AppLayout>
    </FeatureFlagProvider>
  </Suspense>
);

export const authenticatedRoute = createRoute({
  id: AUTH_ROUTE_ID,
  getParentRoute: () => rootRoute,
  beforeLoad: async ({ location, context: { queryClient } }) => {
    if (IS_BONEYARD_BUILD) {
      const token = getBoneyardToken();

      if (token) {
        setupServicesAuth({ token, userId: 'boneyard', queryClient });
        startRecording();
        startApiRecording();

        return { isBoneyardBuild: true };
      }
    }

    const currentUser = await getCurrentUser();

    if (!currentUser?.token) {
      await clearAuthSession(queryClient);
      redirect({
        to: AUTH_ROUTES.login,
        search: { redirect: location.href },
        throw: true,
        ignoreBlocker: true,
        replace: true,
      });

      return;
    }

    const scheduling = {
      queryClient,
      userId: currentUser.id,
      setupServicesAuth,
    };

    if (currentUser.tokenExpiredAt.getTime() <= Date.now()) {
      if (isPastClearDeadline(currentUser)) {
        await clearAuthSession(queryClient);
        redirect({
          to: AUTH_ROUTES.login,
          search: { redirect: location.href },
          throw: true,
          ignoreBlocker: true,
          replace: true,
        });

        return;
      }

      scheduleDeadline(queryClient, currentUser.tokenExpiredAt);
      const canEnter = await dispatchRefreshOutcome(
        scheduling,
        await refreshSessionOnce({ source: 'gate' }),
        {
          authenticated: () => true,
          retryable: () => {
            redirect({ to: AUTH_ROUTES.unauthorized, throw: true });

            return false;
          },
          signedOut: () => {
            redirect({ to: AUTH_ROUTES.login, throw: true });

            return false;
          },
        },
      );

      if (!canEnter) {
        return;
      }
    } else {
      setupServicesAuth({
        token: currentUser.token,
        userId: currentUser.id,
        queryClient,
      });
      scheduleTokenRefresh({
        tokenExpiredAt: currentUser.tokenExpiredAt,
        onRefresh: (scheduledFor) =>
          proactiveTokenRefresh(
            currentUser.id,
            setupServicesAuth,
            queryClient,
            scheduledFor,
          ),
      });
      scheduleDeadline(queryClient, currentUser.tokenExpiredAt);
    }

    startRecording();
    startApiRecording();
  },
  loader: async ({ context: { queryClient } }) => {
    await queryClient.ensureQueryData(getQueryShiftPeriodDisplayTimeOptions());
  },
  component: () => (
    <AuthenticatedLayout>
      <Outlet />
    </AuthenticatedLayout>
  ),
});
